// src/components/GlobalPlayerBar.js
import React, { useRef, useEffect, useState } from 'react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import '../styles/global-player.css';

const GlobalPlayerBar = () => {
  const {
    currentStation,
    isPlaying,
    volume,
    togglePlayback,
    setVolume,
    getTrackInfo,
    getShowTitle,
    isCatchupMode,
    catchupCurrentTime,
    catchupDuration,
    skipForward,
    skipBackward,
    seekToPosition,
    catchupShowArt
  } = useAudioPlayer();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(volume);

  // Add refs for checking text overflow
  const trackTitleRef = useRef(null);
  const [isTrackTitleOverflowing, setIsTrackTitleOverflowing] = useState(false);
  const showTitleRef = useRef(null);
  const [isShowTitleOverflowing, setIsShowTitleOverflowing] = useState(false);

  // Get show data from new API structure
  const showData = getTrackInfo(); // Now returns the full data object
  // Simple image logic
  const showArt = isCatchupMode 
    ? catchupShowArt 
    : (showData?.artwork_urls?.large || showData?.artwork_urls?.standard);
  const trackArtist = showData?.track_artist || '';
  const trackTitle = showData?.track_title || 'No track information';
  const trackAlbum = showData?.track_album || '';

  // Check if text is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      if (trackTitleRef.current) {
        const trackTitleElement = trackTitleRef.current;
        const isOverflowing = trackTitleElement.scrollWidth > trackTitleElement.clientWidth;
        setIsTrackTitleOverflowing(isOverflowing);
      }
      
      if (showTitleRef.current) {
        const showTitleElement = showTitleRef.current;
        const isOverflowing = showTitleElement.scrollWidth > showTitleElement.clientWidth;
        setIsShowTitleOverflowing(isOverflowing);
      }
    };
    
    // Check immediately
    checkOverflow();
    
    // Check on window resize
    window.addEventListener('resize', checkOverflow);
    
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [trackTitleRef.current, showTitleRef.current, showData]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update mute state when volume changes externally
  useEffect(() => {
    setIsMuted(volume === 0);
  }, [volume]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (newVolume > 0) {
      setPreviousVolume(newVolume);
    }
  };

  const handleMuteToggle = () => {
    if (isMuted) {
      // Unmute - restore to previous volume or 0.5 if no previous
      setVolume(previousVolume > 0 ? previousVolume : 0.5);
      setIsMuted(false);
    } else {
      // Mute - store current volume and set to 0
      setPreviousVolume(volume);
      setVolume(0);
      setIsMuted(true);
    }
  };

  const handleProgressClick = (e) => {
    if (!isCatchupMode || !catchupDuration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * catchupDuration;
    seekToPosition(newTime);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Mobile version of the player
  if (isMobile) {
    return (
      <div className={`global-player-bar mobile ${isCatchupMode ? 'catchup-mode' : ''}`}>
        <div className="mobile-player-container">
          {/* Mobile Metadata Row */}
          <div className="mobile-metadata-row">
            {/* Show Art from new API structure */}
            {showArt && (
              <div 
                className="mobile-show-art-thumb"
                style={{ 
                  backgroundImage: `url('${showArt}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            )}
            
            <div className="mobile-track-info">
              <div 
                className="mobile-show-title"
                ref={showTitleRef}
                title={trackTitle || 'Select a station'}
              >
                {trackTitle || 'Select a station'}
              </div>
              <div 
                className="mobile-track-title"
                ref={trackTitleRef}
                title={trackArtist || 'No track information'}
              >
                {trackArtist || 'No track information'}
              </div>
            </div>
          </div>

          {/* Mobile Controls Row */}
          <div className="mobile-controls-row">
            {isCatchupMode ? (
              <div className="mobile-catchup-controls">
                <div className="mobile-catchup-top-row">
                  
                  {/* Left spacer */}
                  <div className="mobile-controls-spacer" />

                  {/* Center controls */}
                  <div className="mobile-catchup-center-controls">
                    <button className="mobile-skip-btn" onClick={() => skipBackward(10)}>
                      <i className="fa-solid fa-arrow-rotate-left"></i>
                    </button>

                    <button 
                      className="mobile-play-pause-btn large"
                      onClick={togglePlayback}
                    >
                      <i className={`fas ${isPlaying ? 'fa-stop' : 'fa-play'}`} />
                    </button>

                    <button className="mobile-skip-btn" onClick={() => skipForward(10)}>
                      <i className="fa-solid fa-arrow-rotate-right"></i>
                    </button>
                  </div>

                  {/* Right mute */}
                  <div className="mobile-right-controls">
                    <button className="mobile-mute-btn" onClick={handleMuteToggle}>
                      <i className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`} />
                    </button>
                  </div>

                </div>
                
                {/* Progress Bar beneath left-aligned controls */}
                <div className="mobile-catchup-progress">
                  <div className="mobile-catchup-time-start">
                    {formatTime(catchupCurrentTime)}
                  </div>
                  <div className="mobile-progress-bar-container">
                    <div className="mobile-progress-bar thin" onClick={handleProgressClick}>
                      <div 
                        className="mobile-progress-fill" 
                        style={{ 
                          width: catchupDuration ? `${(catchupCurrentTime / catchupDuration) * 100}%` : '0%' 
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="mobile-catchup-time-end">
                    {formatTime(catchupDuration)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mobile-live-controls-row">
                {/* Left spacer (keeps center truly centered) */}
                <div className="mobile-controls-spacer" />

                {/* Center controls */}
                <div className="mobile-live-center-controls">
                  <button 
                    className="mobile-play-pause-btn large"
                    onClick={togglePlayback}
                    disabled={!currentStation}
                    aria-label={isPlaying ? "Stop" : "Play"}
                  >
                    <i className={`fas ${isPlaying ? 'fa-stop' : 'fa-play'}`} />
                  </button>
                </div>

                {/* Right mute */}
                <div className="mobile-right-controls">
                  <button 
                    className="mobile-mute-btn"
                    onClick={handleMuteToggle}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    <i className={`fas ${isMuted ? 'fa-volume-mute' : volume > 0.5 ? 'fa-volume-up' : 'fa-volume-down'}`} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop version
  return (
    <div className={`global-player-bar desktop ${isCatchupMode ? 'catchup-mode' : ''}`}>
      <div className="player-bar-content">
        <div className="player-bar-left">
          {/* Show track/show art from new API structure */}
          {showArt && (
            <div 
              className={`global-show-art ${isCatchupMode ? 'catchup-mode' : ''}`}
              style={{ 
                backgroundImage: `url('${showArt}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
          )}
          
          {/* Track info with artist and album from new API */}
          <div className="player-bar-track-info">
            <div 
              id="global-show-title" 
              className={`global-show-title ${isShowTitleOverflowing ? 'scroll-text' : ''}`}
              ref={showTitleRef}
              title={trackTitle || 'Select a station'}
            >
              <span>{trackTitle || 'Select a station'}</span>
            </div>
            <div 
              id="global-track-title" 
              className={`global-track-title ${isTrackTitleOverflowing ? 'scroll-text' : ''}`}
              ref={trackTitleRef}
              title={trackArtist || 'No track information'}
            >
              <span>
                {trackArtist || 'No track information'}
                {trackAlbum && trackAlbum !== 'Unknown' && ` (${trackAlbum})`}
              </span>
            </div>
          </div>
        </div>
        
        <div className="player-bar-middle">
          {/* Conditionally render catchup or live controls */}
          {isCatchupMode ? (
            <div className="catchup-controls">
              {/* Skip buttons */}
              <div className="catchup-skip-controls">
                <button 
                  className="catchup-skip-btn" 
                  onClick={() => skipBackward(10)}
                  title="Skip back 10 seconds"
                  disabled={!catchupDuration}
                >
                  <i className="fa-solid fa-arrow-rotate-left"></i>
                  <span>10s</span>
                </button>
                
                {/* Play/Pause button */}
                <button 
                  id="global-play-pause-btn" 
                  className="global-play-pause-btn"
                  onClick={togglePlayback}
                  disabled={!currentStation}
                >
                  {isPlaying ? (
                    <i className="fas fa-stop" />
                  ) : (
                    <i className="fas fa-play" />
                  )}
                </button>
                
                <button 
                  className="catchup-skip-btn" 
                  onClick={() => skipForward(10)}
                  title="Skip forward 10 seconds"
                  disabled={!catchupDuration}
                >
                  <i className="fa-solid fa-arrow-rotate-right"></i>
                  <span>10s</span>
                </button>

                {/* Progress bar for catchup */}
                <div className="catchup-progress-container">
                  <div className="catchup-progress-bar" onClick={handleProgressClick}>
                    <div 
                      className="catchup-progress" 
                      style={{ 
                        width: catchupDuration ? `${(catchupCurrentTime / catchupDuration) * 100}%` : '0%' 
                      }}
                    ></div>
                  </div>
                  <div className="catchup-time-info">
                    <span>{formatTime(catchupCurrentTime)}</span>
                    <span>{formatTime(catchupDuration)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="global-controls">
              <button 
                id="global-play-pause-btn" 
                className="global-play-pause-btn"
                onClick={togglePlayback}
                disabled={!currentStation}
              >
                {isPlaying ? (
                  <i className="fas fa-stop" />
                ) : (
                  <i className="fas fa-play" />
                )}
              </button>
            </div>
          )}
        </div>
        
        <div className="player-bar-right">
          <div className="global-volume-control">
            <i className={`fas ${volume === 0 ? 'fa-volume-mute' : 'fa-volume-up'}`} />
            <input 
              type="range" 
              id="global-volume-slider" 
              className="global-volume-slider" 
              min="0" 
              max="1" 
              step="0.01" 
              value={volume}
              onChange={handleVolumeChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalPlayerBar;